/**
 * Entity Details Modal Component
 * Full-featured modal for viewing entity details with filtering, sorting, and pagination
 */

import React, { useMemo, useState } from 'react';
import { X, Filter, AlertCircle } from 'lucide-react';
import { EntityTable } from './EntityTable';
import { EntityFilters } from './EntityFilters';
import { getPrioritizedFields } from '../../utils/entityFieldPrioritizer';
import { detectFieldType, getSortFunction } from '../../utils/entityFieldFormatter';
import { getArtifact } from '../../api/artifacts';

export interface EntityDetail {
  id: string;
  data: any;
  source_artifact_id?: string;
}

interface EntityDetailsModalProps {
  entityType: string;
  entityLabel: string;
  entities: EntityDetail[];
  loading: boolean;
  onClose: () => void;
  fieldPriorityConfig?: any;
}

export function EntityDetailsModal({
  entityType,
  entityLabel,
  entities,
  loading,
  onClose,
  fieldPriorityConfig,
}: EntityDetailsModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Sorting and filtering state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());
  const [artifactDetails, setArtifactDetails] = useState<Record<string, any>>({});
  const [loadingArtifacts, setLoadingArtifacts] = useState<Set<string>>(new Set());

  // Get prioritized fields
  const fields = useMemo(() => {
    return getPrioritizedFields(entities, fieldPriorityConfig);
  }, [entities, fieldPriorityConfig]);

  // Apply filters and sorting
  const filteredAndSortedEntities = useMemo(() => {
    let result = [...entities];

    // Apply filters
    if (Object.keys(filters).length > 0) {
      result = result.filter(entity => {
        return Object.entries(filters).every(([key, filterValue]) => {
          const entityValue = entity.data?.[key];
          
          if (filterValue === null || filterValue === '') return true;
          
          if (filterValue === 'null') {
            return entityValue === null || entityValue === undefined;
          }
          
          if (typeof filterValue === 'object' && filterValue.min !== undefined) {
            // Numeric range filter
            const numValue = Number(entityValue);
            if (isNaN(numValue)) return false;
            return numValue >= filterValue.min && numValue <= filterValue.max;
          }
          
          // String/boolean/enum filter
          return String(entityValue) === String(filterValue);
        });
      });
    }

    // Apply sorting
    if (sortColumn) {
      const fieldMeta = entities.length > 0 
        ? detectFieldType(sortColumn, entities[0].data?.[sortColumn])
        : null;
      
      if (fieldMeta) {
        const sortFn = getSortFunction(sortColumn, fieldMeta.type);
        result.sort((a, b) => {
          const comparison = sortFn(a, b);
          return sortDirection === 'asc' ? comparison : -comparison;
        });
      }
    }

    return result;
  }, [entities, filters, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedEntities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntities = filteredAndSortedEntities.slice(startIndex, endIndex);

  // Handle column sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (column: string, value: any) => {
    setFilters(prev => {
      if (value === null || value === '' || value === 'all') {
        const newFilters = { ...prev };
        delete newFilters[column];
        return newFilters;
      }
      return { ...prev, [column]: value };
    });
    setCurrentPage(1);
  };

  // Toggle expanded cell
  const toggleExpanded = (entityId: string, column: string) => {
    const cellKey = `${entityId}-${column}`;
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellKey)) {
        newSet.delete(cellKey);
      } else {
        newSet.add(cellKey);
      }
      return newSet;
    });
  };

  // Toggle artifact expansion
  const handleToggleArtifact = async (artifactId: string) => {
    const isExpanded = expandedArtifacts.has(artifactId);
    
    if (isExpanded) {
      // Collapse
      setExpandedArtifacts(prev => {
        const next = new Set(prev);
        next.delete(artifactId);
        return next;
      });
    } else {
      // Expand - fetch artifact details if not already loaded
      setExpandedArtifacts(prev => new Set(prev).add(artifactId));
      
      if (!artifactDetails[artifactId] && !loadingArtifacts.has(artifactId)) {
        setLoadingArtifacts(prev => new Set(prev).add(artifactId));
        try {
          const artifact = await getArtifact(artifactId);
          setArtifactDetails(prev => ({
            ...prev,
            [artifactId]: artifact
          }));
        } catch (error) {
          console.error('Failed to fetch artifact:', error);
          setArtifactDetails(prev => ({
            ...prev,
            [artifactId]: { error: 'Failed to load' }
          }));
        } finally {
          setLoadingArtifacts(prev => {
            const next = new Set(prev);
            next.delete(artifactId);
            return next;
          });
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] max-w-[95vw] max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {entityLabel}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {entities.length.toLocaleString()} entities • Type: {entityType}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 flex flex-col overflow-hidden p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : entities.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">No entities found for this type.</p>
            </div>
          ) : (
            <div className="flex flex-col space-y-4 h-full overflow-hidden">
              {/* Filter Toggle */}
              <div className="flex items-center justify-between flex-shrink-0">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Showing {filteredAndSortedEntities.length.toLocaleString()} of {entities.length.toLocaleString()} entities
                  {Object.keys(filters).length > 0 && (
                    <span className="ml-2 text-blue-600">
                      ({Object.keys(filters).length} filter{Object.keys(filters).length !== 1 ? 's' : ''} applied)
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Filter className="h-4 w-4" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
              </div>

              {/* Filters Row */}
              {showFilters && (
                <EntityFilters
                  entities={entities}
                  fields={fields}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              )}

              {/* Table */}
              <EntityTable
                entities={paginatedEntities}
                fields={fields}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                filters={filters}
                expandedCells={expandedCells}
                expandedArtifacts={expandedArtifacts}
                artifactDetails={artifactDetails}
                loadingArtifacts={loadingArtifacts}
                onSort={handleSort}
                onToggleExpanded={toggleExpanded}
                onToggleArtifact={handleToggleArtifact}
              />
            </div>
          )}
        </div>

        {/* Modal Footer with Pagination */}
        {filteredAndSortedEntities.length > itemsPerPage && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedEntities.length)} of{' '}
              {filteredAndSortedEntities.length.toLocaleString()} entities
              {filteredAndSortedEntities.length !== entities.length && (
                <span className="text-gray-400"> (filtered from {entities.length.toLocaleString()})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}











